import { useIsomorphicLayoutEffect } from '@lemonade-hq/cdk';
import { useCallback, useState } from 'react';
import { BREAKPOINTS } from '../config/responsiveness';

export interface ViewportProps {
  readonly width: number;
  readonly height: number;
  readonly matchingBreakpoint: number;
  readonly isMobileViewport: boolean;
  readonly isTabletViewport: boolean;
  readonly isBelowDesktopViewport: boolean;
  readonly isDesktopViewport: boolean;
  readonly isAboveDesktopViewport: boolean;
  readonly isWideDesktopViewport: boolean;
}

const breakpoints = Object.values(BREAKPOINTS);

const getPageSize = (): ViewportProps => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const matchingBreakpoint: number = breakpoints.reduceRight(
    (acc: number, breakpointWidth: number | string, index: number): number => {
      if (width < (breakpointWidth as unknown as number)) {
        return index - 1;
      }

      return acc;
    },
    breakpoints.length - 1,
  );

  const isMobileViewport = matchingBreakpoint === 0;
  const isTabletViewport = matchingBreakpoint === 1;
  const isBelowDesktopViewport = matchingBreakpoint < 2;
  const isDesktopViewport = matchingBreakpoint === 2;
  const isAboveDesktopViewport = matchingBreakpoint >= 2;
  const isWideDesktopViewport = matchingBreakpoint >= 3;

  return {
    width,
    height,
    isMobileViewport,
    isTabletViewport,
    isBelowDesktopViewport,
    isDesktopViewport,
    isAboveDesktopViewport,
    isWideDesktopViewport,
    matchingBreakpoint,
  };
};

export const useViewportSize = (): ViewportProps => {
  const [
    {
      width,
      height,
      isMobileViewport,
      isTabletViewport,
      isBelowDesktopViewport,
      isDesktopViewport,
      isAboveDesktopViewport,
      isWideDesktopViewport,
      matchingBreakpoint,
    },
    setState,
  ] = useState(getPageSize());

  const handler = useCallback(() => {
    setState(getPageSize());
  }, []);

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('resize', handler, { passive: true });
    setState(getPageSize());
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  return {
    width,
    height,
    isMobileViewport,
    isTabletViewport,
    isBelowDesktopViewport,
    isDesktopViewport,
    isAboveDesktopViewport,
    isWideDesktopViewport,
    matchingBreakpoint,
  };
};
